// PendingApprovalPage.jsx
// Shown to a signed-in user whose account has not been approved yet.
//
// WHY THIS SCREEN EXISTS
// Self-registration creates an auth.users row, and migration 0003's trigger parks the
// matching public.users row in the holding tenant with active = false. The access-token
// hook then injects no tenant claim, so every RLS policy matches nothing.
//
// Without this screen that user sees the full application with every page empty — no
// error, no explanation, nothing to act on. That is the "empty screen, no exception"
// failure mode CLAUDE.md warns about, shown to the person least equipped to diagnose it.
// They would reasonably conclude the product is broken.
//
// So the state gets named. The user is told they are in a queue, not looking at a bug.

import React from 'react';
import { useAuth } from '../context/AuthContext';
import './PendingApprovalPage.css';
import LegalLinks from '../legal/LegalLinks';

export default function PendingApprovalPage() {
  const { user, logout, adminsNotified, emailConfirmed } = useAuth();
  const email = user?.email || '';

  // TWO SEQUENTIAL WAITS, AND SAYING THE WRONG ONE IS WORSE THAN SAYING NOTHING (13.12).
  //
  // *Confirm email* is ON in the Supabase project, so an email/password sign-up lands
  // here BEFORE the address is proved: 0003's trigger fires `after insert`, which is
  // before the link is clicked. Task 13.2 withholds the admin notification until then,
  // deliberately — otherwise every abandoned form mails both administrators.
  //
  // So a user in that state is waiting on THEMSELVES, not on us. Telling them an
  // administrator is reviewing their request would be false, and it would stop them
  // checking their inbox, which is the one action that moves them forward. This is the
  // same argument as the adminsNotified flag below: never claim a step has happened
  // when it has not.
  //
  // OAuth users never see this branch — the provider has already verified the address,
  // and the server defaults emailConfirmed to true for every identity but an
  // unconfirmed email/password one.
  if (!emailConfirmed) {
    return (
      <div className="pa-wrap">
        <div className="pa-card">
          <div className="pa-icon" aria-hidden="true">📬</div>

          <h1 className="pa-title">Confirm your email address</h1>

          <p className="pa-body">
            We've sent a confirmation link{email ? <> to <strong>{email}</strong></> : ''}.
            Click it to activate your account.
          </p>

          <p className="pa-body pa-body--muted">
            Nothing has gone wrong — this is the first of two steps. Once your address is
            confirmed, an administrator is asked to approve your access.
          </p>

          <div className="pa-note">
            Already clicked the link? Sign out and back in — the confirmation applies
            when you get a new session.
          </div>

          <button type="button" className="pa-btn" onClick={logout}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pa-wrap">
      <div className="pa-card">
        <div className="pa-icon" aria-hidden="true">⏳</div>

        <h1 className="pa-title">Your account is awaiting approval</h1>

        <p className="pa-body">
          You're signed in{email ? <> as <strong>{email}</strong></> : ''}, but an
          administrator needs to grant access before you can use DigitalMe.
        </p>

        <p className="pa-body pa-body--muted">
          This is expected for a new account — nothing has gone wrong. You'll be able to
          sign in normally once your access has been approved.
        </p>

        {/* Only claimed when the server actually said so. Telling someone "we've let
            the team know" when the mail failed is worse than saying nothing: they stop
            chasing it. */}
        {adminsNotified && (
          <p className="pa-body pa-body--confirm">
            ✓ The team has been notified that you're waiting.
          </p>
        )}

        {/* Signing out matters here: approval changes the tenant on the user's row, but
            tenant_id is stamped into the JWT when the token is minted. Until they get a
            NEW token the app would still show nothing, which would look like the
            approval had not worked. */}
        <div className="pa-note">
          Already been approved? Sign out and back in — your access is applied when you
          get a new session.
        </div>

        <button type="button" className="pa-btn" onClick={logout}>
          Sign out
        </button>

        <LegalLinks className="pa-legal" />
      </div>
    </div>
  );
}
